import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import {
  formatFechaAsignacion,
  getUsuarioId,
  getUsuarioNombre,
} from "../usuariosProyectosUtils";

const UsuariosProyectoTable = ({
  rows = [],
  onRemove,
  showProject = false,
  projectCounts = {},
  paginated = false,
}) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    setPage(0);
  }, [rows]);

  const visibleRows = useMemo(() => {
    if (!paginated) return rows;
    const start = page * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [page, paginated, rows, rowsPerPage]);

  return (
    <Box
      sx={{
        width: "100%",
        overflow: "hidden",
        bgcolor: theme.palette.background.alt,
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        boxShadow: 1,
      }}
    >
      <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
        <Table size="small" aria-label="Usuarios asignados a proyectos">
          <TableHead
            sx={{
              "& .MuiTableCell-head": {
                bgcolor: theme.palette.background.alt,
                borderBottom: 1,
                borderColor: "divider",
                py: 1.5,
                whiteSpace: "nowrap",
              },
            }}
          >
            <TableRow>
              {showProject && <TableCell sx={{ fontWeight: 800 }}>Proyecto</TableCell>}
              <TableCell sx={{ fontWeight: 800 }}>Usuario</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Correo</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Rol</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Cargo</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Estado</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Asignado desde</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800 }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((row, rowIndex) => {
              const userId = String(getUsuarioId(row));
              const projectCount = Number(projectCounts[userId] || 0);

              return (
                <TableRow
                  key={`${row.idProyecto || "project"}-${userId}`}
                  hover
                  sx={{
                    bgcolor:
                      rowIndex % 2 === 0
                        ? theme.palette.background.light
                        : theme.palette.background.alt,
                    "&.MuiTableRow-hover:hover": {
                      bgcolor: theme.palette.action.hover,
                    },
                    "& .MuiTableCell-root": {
                      borderColor: "divider",
                      py: 1.25,
                    },
                  }}
                >
                  {showProject && (
                    <TableCell sx={{ minWidth: 190 }}>
                      <Typography variant="body2" fontWeight={700}>
                        {[row.codigoProyecto, row.nombreProyecto].filter(Boolean).join(" · ")}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell sx={{ minWidth: 165 }}>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography variant="body2" fontWeight={700}>
                        {getUsuarioNombre(row)}
                      </Typography>
                      {showProject && projectCount > 1 && (
                        <Chip label={`${projectCount} proyectos`} size="small" variant="outlined" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{row.email || "—"}</TableCell>
                  <TableCell>{row.nombreRol || row.rol || "—"}</TableCell>
                  <TableCell>{row.cargo || "—"}</TableCell>
                  <TableCell>
                    <Chip
                      label={Number(row.estadoUsuario) === 1 ? "Activo" : "Inactivo"}
                      size="small"
                      sx={{
                        bgcolor: Number(row.estadoUsuario) === 1 ? "green" : "firebrick",
                        color: "#fff",
                        height: 24,
                        borderRadius: "0.25rem",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {formatFechaAsignacion(row.fechaRegistro)}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Quitar del proyecto">
                      <IconButton
                        aria-label={`Quitar a ${getUsuarioNombre(row)} del proyecto`}
                        color="error"
                        size="small"
                        onClick={() => onRemove?.(row)}
                      >
                        <PersonRemoveOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={showProject ? 8 : 7} align="center" sx={{ py: 5 }}>
                  No hay usuarios para mostrar.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {paginated && (
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100]}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{
            bgcolor: theme.palette.background.alt,
            borderTop: 1,
            borderColor: "divider",
          }}
        />
      )}
    </Box>
  );
};

export default UsuariosProyectoTable;
